import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { StoreModel } from 'src/app/models/archive/store.model';
import { StoreService } from 'src/app/services/archive/store.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-store',
  templateUrl: './register-store.component.html',
  styleUrls: ['./register-store.component.scss'],
  standalone: false
})
export class RegisterStoreComponent implements OnInit {

  branchId: number = 0;
  disabledForm: boolean = false;
  formStore: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listInventories: any[] = [];
  nameWindow: string = 'Captura Bodegas';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  severityState: string = 'success';
  stateStore: string = '';
  storeContract: StoreModel = new StoreModel();
  storeId: number = 0;
  titleState: string = '';
  url: string = 'store';

  constructor(private activatedRoute: ActivatedRoute,
    private branchService: BranchService,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private headerService: HeaderService,
    private location: Location,
    private storeService: StoreService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
    this.validateForm(new StoreModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.storeId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  changeBranch(branchId: number) {
    this.storeContract = new StoreModel();
    this.storeContract.branchId = branchId;
    this.validateForm(this.storeContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async changeCode(code: number) {
    if (!code || this.storeContract.code === code) { return; }
    const branchId = this.formStore.get('branchId')?.value;
    if (!branchId) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
      return;
    }
    try {
      const response = await firstValueFrom(this.storeService.getStoreByCode(branchId, code));
      this.storeId = response.storeId;
      this.storeContract = response;
      this.validateForm(this.storeContract);
      this.location.replaceState(`/${this.url}/register/${this.storeId}`);
    } catch (error) {
      console.error('Error al buscar la bodega por código.', error);
    }
  }

  async changeStateStore() {
    const { storeId } = this.storeContract;
    if (!storeId || storeId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear una bodega.'
      );
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `No tiene permisos para cambiar el estado de la bodega. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    try {
      const response = await firstValueFrom(this.storeService.setActiveStore(storeId));
      this.storeContract = response;
      this.validateForm(this.storeContract);
      const successMessage = response.active
        ? 'La bodega fue activada exitosamente.'
        : 'La bodega fue inactivada exitosamente.';
      this.toastHandlerService.showToast('success', successMessage);
    } catch (error) {
      console.error('Error al cambiar el estado de la bodega.', error);
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear bodegas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.storeId = 0;
    this.storeContract = new StoreModel();
    this.storeContract.branchId = this.branchId;
    this.validateForm(this.storeContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar bodegas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.storeId || this.storeId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear una bodega.'
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la bodega?')) {
      try {
        await firstValueFrom(this.storeService.deleteStore(this.storeContract.storeId));
        this.createNewRegister();
        this.toastHandlerService.showToast(
          'success',
          'La bodega fue eliminada exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar la bodega.', error);
      }
    }
  }

  async getAllLists() {
    try {
      const [branches, inventories] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.storeService.getAllInventoriesForSelectAsync()),
      ]);
      this.listBranches = branches;
      this.listInventories = inventories;
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    const branchId = this.formStore.get("branchId")?.value;
    const code = this.formStore.get("code")?.value;
    if (branchId == null || branchId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
      return;
    }
    this.handleStoreNavigation(buttonClick, branchId, code)
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const response = await firstValueFrom(
        this.userAuthenticatedService.branchObservable.pipe(
          filter(branch => !!branch && branch.branchId !== 0)
        )
      );
      this.branchId = response.branchId;
    } catch (error) {
      console.error('Error al obtener la primera sucursal del usuario autenticado.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getFirstBranchByUserAuthenticatedAsync(),
      this.getPermissionProfileByUrl(),
      this.getAllLists(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getStoreById() {
    try {
      const response = await firstValueFrom(this.storeService.getStoreById(this.storeId));
      this.storeContract = response;
      this.validateForm(response);
      this.location.replaceState(`/${this.url}/register/${this.storeId}`);
    } catch (error) {
      console.error('Error al obtener la bodega por id.', error);
    }
  }

  async handleInitialState() {
    if (this.storeId !== null && this.storeId !== 0) {
      await this.getStoreById();
    } else {
      this.createNewRegister();
    }
  }

  async handleStoreNavigation(buttonClick: any, branchId: number, code: number) {
    const actionMap: Record<string, () => Promise<StoreModel>> = {
      first: async () =>
        await firstValueFrom(this.storeService.getFirstStoreByKeysAsync(branchId)),
      previus: async () =>
        await firstValueFrom(this.storeService.getPreviusStoreByKeysAsync(branchId, code)),
      next: async () =>
        await firstValueFrom(this.storeService.getNextStoreByKeysAsync(branchId, code)),
      last: async () =>
        await firstValueFrom(this.storeService.getLastStoreByKeysAsync(branchId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.storeId = 0;
      this.storeContract = new StoreModel();
      this.storeContract.branchId = branchId;
      return;
    }
    try {
      const store = await action();
      this.storeId = store.storeId;
      this.storeContract = store;
      this.validateForm(this.storeContract);
      this.location.replaceState(`/${this.url}/register/${this.storeId}`);
    } catch (error) {
      this.storeContract = new StoreModel();
      this.storeContract.storeId = 0;
      this.storeContract.branchId = branchId;
      this.storeContract.code = code;
      console.error(`Error al buscar la bodega (${buttonClick.target.id}).`, error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formStore.valid) {
      const modelStore = this.formStore.getRawValue() as StoreModel;
      if (modelStore.alterCode == "") modelStore.alterCode = null;
      if (modelStore.costCenterCode == "") modelStore.costCenterCode = null;
      if (this.storeId == 0) {
        try {
          const response = await firstValueFrom(this.storeService.createStore(modelStore));
          this.storeId = response.storeId;
          this.storeContract = response;
          this.validateForm(this.storeContract);
          this.location.replaceState(`/${this.url}/register/${this.storeId}`);
          this.toastHandlerService.showToast(
            'success',
            'La bodega fue creada exitosamente.'
          );
        } catch (error) {
          console.error('Error al crear la bodega.', error);
        }
      } else {
        this.storeContract.branchId = modelStore.branchId;
        this.storeContract.inventoryId = modelStore.inventoryId;
        this.storeContract.alterCode = modelStore.alterCode;
        this.storeContract.name = modelStore.name;
        this.storeContract.occupancyPercentageReport = modelStore.occupancyPercentageReport;
        this.storeContract.costCenterCode = modelStore.costCenterCode;
        this.storeContract.ownStore = modelStore.ownStore;
        this.storeContract.swMandate = modelStore.swMandate;
        try {
          const response = await firstValueFrom(this.storeService.updateStore(this.storeContract));
          this.storeContract = response;
          this.validateForm(this.storeContract);
          this.toastHandlerService.showToast(
            'success',
            'La bodega fue modificada exitosamente.'
          );
        } catch (error) {
          console.error('Error al actualizar la bodega.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      inventoryId: {
        required: 'El campo Inventario es obligatorio.',
        min: 'Debe seleccionar un Inventario válido.'
      },
      code: {
        required: 'El campo Código es obligatorio.'
      },
      alterCode: {
        maxlength: 'El Código alterno no puede tener más de 20 caracteres.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 50 caracteres.'
      },
      costCenterCode: {
        maxlength: 'El Código del centro de costos no puede tener más de 10 caracteres.'
      },
      ownStore: {
        required: 'Debe indicar si la bodega es propia o no.'
      },
      active: {
        required: 'Debe indicar si la bodega está activa o inactiva.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formStore.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormState() {
    if (this.storeContract.active) {
      this.disabledForm = false;
      this.titleState = 'inactivar';
      this.severityState = 'success';
      this.stateStore = 'Activo';
      setTimeout(() => this.formStore.enable());
      const controlCode = this.formStore.controls['code'];
      setTimeout(() => this.storeContract.storeId != 0 ?
        controlCode.enable() : controlCode.disable());
    } else {
      this.disabledForm = true;
      this.titleState = 'Activar';
      this.severityState = 'danger';
      this.stateStore = 'Inactivo';
      setTimeout(() => {
        this.formStore.disable(),
          this.formControlStateService.setControlState(this.formStore, ['branchId', 'code'], true)
      });
    }
    if (!this.permissionUpdatable) {
      this.disabledForm = true;
      setTimeout(() => this.formStore.disable());
    }
  }

  validateForm(store: StoreModel) {
    this.formStore = this.formBuilder.group({
      branchId: [store.branchId, [Validators.required, Validators.min(1)]],
      inventoryId: [store.inventoryId, [Validators.required, Validators.min(1)]],
      code: [store.code, [Validators.required]],
      alterCode: [store.alterCode, [Validators.maxLength(20)]],
      name: [store.name, [Validators.required, Validators.maxLength(50)]],
      occupancyPercentageReport: [store.occupancyPercentageReport],
      costCenterCode: [store.costCenterCode, [Validators.maxLength(10)]],
      ownStore: [store.ownStore],
      swMandate: [store.swMandate],
      active: [store.active]
    });
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formStore.controls).forEach((key) => {
      const control = this.formStore.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
